import React, { useEffect, useState } from "react";
import { RichTreeView } from "@mui/x-tree-view";
import { TextField, IconButton, Box, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import ApiService from "../../../utils/fetch";
import {
  setMFTemplateTreeData,
  setMFTemplateSelectedIds,
  setMFTemplateAppliedData,
  setMFTemplateExpandedItems,
} from "../../../redux/MFTemplateTreeSlice";
import ButtonComponent from "../../../Components/Common/button/Button";

const MFPlantSelectionTree = ({
  title,
  onApplySelection,
  containerHeight,
}) => {
  const dispatch = useDispatch();

  const { MFTemplateTreeData, MFTemplateExpandedItems } = useSelector(
    (state) => state.MFTemplateTree,
  );

  const [treeData, setTreeData] = useState([]);
  const [filteredTreeData, setFilteredTreeData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSelectionApplied, setIsSelectionApplied] = useState(false);

  /* ===================== TRANSFORM DATA ===================== */
  const transformData = (data) =>
    data.map((bottlerItem, bIndex) => ({
      id: `bottler${bIndex + 1}`,
      label: `${bottlerItem.bottler} - ${bottlerItem.bottlerOwnerCode}${
        bottlerItem.bottlerOwnerDescription
          ? ` - ${bottlerItem.bottlerOwnerDescription}`
          : ""
      }`,
      bottler: bottlerItem.bottler,
      bottlerOwnerCode: bottlerItem.bottlerOwnerCode,
      bottlerOwnerDescription: bottlerItem.bottlerOwnerDescription || "", // ← Added
      children: (bottlerItem.profitCenterDescription || []).map(
        (pcDesc, pIndex) => ({
          id: `pcCountry${bIndex + 1}-${pIndex + 1}`,
          label: pcDesc,
          profitCenterCountry: pcDesc,
        }),
      ),
    }));

  /* ===================== FETCH DATA ===================== */
  useEffect(() => {
    if (!MFTemplateTreeData || MFTemplateTreeData.length === 0) {
      ApiService.get("/v1/custom-market-forecast/configuration-bottler-selection")
        .then((res) => {
          const transformed = transformData(res.data || []);
          setTreeData(transformed);
          setFilteredTreeData(transformed);
          dispatch(setMFTemplateTreeData(transformed));
        })
        .catch(console.error);
    } else {
      setTreeData(MFTemplateTreeData);
      setFilteredTreeData(MFTemplateTreeData);
    }

    return () => {
      dispatch(setMFTemplateSelectedIds([]));
      dispatch(setMFTemplateAppliedData([]));
      dispatch(setMFTemplateExpandedItems([]));
    };
  }, [MFTemplateTreeData, dispatch]);

  /* ===================== SELECTION HANDLER ===================== */
  const handleSelectedItemsChange = (_event, ids) => {
    const newSelectedIds = determineIdsToSet(filteredTreeData, ids, selectedIds);
    setSelectedIds(newSelectedIds);
    setIsSelectionApplied(false);
    dispatch(setMFTemplateSelectedIds(newSelectedIds));
  };

  /* ===================== EXPAND HANDLER ===================== */
  const handleExpandedItemsChange = (_event, expandedItems) => {
    dispatch(setMFTemplateExpandedItems(expandedItems));
  };

  /* ===================== APPLY ===================== */
  const handleApply = () => {
    const pcCountryIds = selectedIds.filter((id) => id.startsWith("pcCountry"));

    const appliedSelectedData = buildAppliedSelectedData(
      pcCountryIds,
      treeData,
    );

    dispatch(setMFTemplateAppliedData(appliedSelectedData));
    setIsSelectionApplied(appliedSelectedData.length > 0);
    onApplySelection?.(appliedSelectedData);
  };

  /* ===================== HELPERS ===================== */

  function determineIdsToSet(items, newIds, currentIds) {
    const isDeselecting = currentIds.length > newIds.length;

    if (isDeselecting) {
      const removed = currentIds.find((id) => !newIds.includes(id));
      const parentIds = getAllParentIds(items, removed);
      const childIds = getSelectedIdsAndChildrenIds(items, [removed]);

      return newIds.filter(
        (id) => !parentIds.includes(id) && !childIds.includes(id),
      );
    }

    const idsToSet = getSelectedIdsAndChildrenIds(items, newIds);
    let parent = getParentNode(items, newIds[newIds.length - 1]);

    while (parent) {
      const childIds = parent.children?.map((c) => c.id) ?? [];
      if (childIds.every((id) => idsToSet.includes(id))) {
        if (!idsToSet.includes(parent.id)) idsToSet.push(parent.id);
        parent = getParentNode(items, parent.id);
      } else break;
    }

    return [...new Set(idsToSet)];
  }

  function getSelectedIdsAndChildrenIds(items, selectedIds) {
    const result = new Set(selectedIds);

    items.forEach((item) => {
      if (selectedIds.includes(item.id)) {
        result.add(item.id);
        if (item.children) {
          getSelectedIdsAndChildrenIds(
            item.children,
            item.children.map((c) => c.id),
          ).forEach((id) => result.add(id));
        }
      } else if (item.children) {
        getSelectedIdsAndChildrenIds(item.children, selectedIds).forEach((id) =>
          result.add(id),
        );
      }
    });

    return [...result];
  }

  function getParentNode(items, id) {
    for (const item of items) {
      if (item.children?.some((c) => c.id === id)) return item;
      if (item.children) {
        const parent = getParentNode(item.children, id);
        if (parent) return parent;
      }
    }
    return null;
  }

  function getAllParentIds(items, id) {
    const parents = [];
    let parent = getParentNode(items, id);
    while (parent) {
      parents.push(parent.id);
      parent = getParentNode(items, parent.id);
    }
    return parents;
  }

  /* ===================== BUILD APPLIED DATA (Updated) ===================== */
  const buildAppliedSelectedData = (pcCountryIds, data) => {
    return data.flatMap((bottler) =>
      bottler.children
        .filter((pc) => pcCountryIds.includes(pc.id))
        .map((pc) => ({
          customerCountry: pc.profitCenterCountry,        // as per your change
          bottler: bottler.bottler,
          bottlerOwnerCode: bottler.bottlerOwnerCode,
          bottlerOwnerDescription: bottler.bottlerOwnerDescription, // ← Added
        })),
    );
  };

  /* ===================== SEARCH ===================== */
  const filterTreeData = (items, query) =>
    items
      .filter((item) => {
        const match = item.label.toLowerCase().includes(query.toLowerCase());
        const children = item.children
          ? filterTreeData(item.children, query)
          : [];
        return match || children.length > 0;
      })
      .map((item) => {
        const match = item.label.toLowerCase().includes(query.toLowerCase());
        const children = match
          ? item.children
          : item.children
          ? filterTreeData(item.children, query)
          : [];
        return { ...item, children };
      });

  const getExpandedIdsForSearch = (items) => {
    const ids = new Set();
    const traverse = (nodes, parents = []) => {
      nodes.forEach((node) => {
        if (node.label.toLowerCase().includes(searchQuery.toLowerCase())) {
          parents.forEach((p) => ids.add(p));
        }
        if (node.children) {
          traverse(node.children, [...parents, node.id]);
        }
      });
    };
    traverse(items);
    return [...ids];
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    const filtered = filterTreeData(treeData, value);
    setFilteredTreeData(filtered);

    if (value) {
      dispatch(setMFTemplateExpandedItems(getExpandedIdsForSearch(filtered)));
    }
  };

  /* ===================== UI ===================== */
  return (
    <div className="container">
      <Box
        sx={{
          height: containerHeight ? `${containerHeight}px` : "auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Typography variant="body2" sx={{ fontStyle: "italic", color: "red" }}>
          Ensure you select a combination before uploading the file to set
          column positions.
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            pb: 0,
          }}
        >
          <Typography variant="body1">{title}</Typography>
          <ButtonComponent
            label="Apply"
            bgColor={isSelectionApplied ? "black" : "grey"}
            maxWidth="150px"
            onClick={handleApply}
          />
        </Box>

        <Box sx={{ p: 2, pt: 0, mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <IconButton size="small">
                  <SearchIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </Box>

        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", p: 2, pt: 0 }}>
          <RichTreeView
            multiSelect
            checkboxSelection
            items={filteredTreeData}
            selectedItems={selectedIds}
            onSelectedItemsChange={handleSelectedItemsChange}
            expandedItems={MFTemplateExpandedItems}
            onExpandedItemsChange={handleExpandedItemsChange}
          />
        </Box>
      </Box>
    </div>
  );
};

export default MFPlantSelectionTree;